import { Box, Button, Checkbox, Flex } from '@chakra-ui/react';
import { FunctionComponent, useState } from 'react';
import { useAuth } from '@/auth';
import { ErrorText, Hint, StepProgress, StepTask } from '@/components';
import { STEP_PROGRESS_STATE, TaskState } from '@/constants';
import { useGenerateMetaMutation } from '@/queries';
import { useAppSelector } from '@/redux';
import { getMetaProcessingErrorMessage, parseMeta } from '@/services/metaProcessingApi';
import { GeneratedMeta, MetaProcessRequest } from '@/types/metaProcessing';

type Props = {
  hasMeta: boolean;
  isReviewed: boolean;
  onReviewedChange: (isReviewed: boolean) => void;
  onGenerated: (result: GeneratedMeta) => void;
};

/** Which group/wave to generate the meta for; null until the user, client and wave are all known. */
const useMetaTarget = (): MetaProcessRequest | null => {
  const { user } = useAuth();
  const selectedClient = useAppSelector((state) => state.wave.selectedClient);
  const selectedWaveOption = useAppSelector((state) => state.wave.selectedWaveOption);

  if (!user?.email || !selectedClient || !selectedWaveOption) return null;
  return { userEmail: user.email, groupId: selectedClient.groupId, waveNumber: selectedWaveOption };
};

/** Always-visible checklist of the meta processing tasks. */
export const MetaProgressPanel: FunctionComponent<Props> = ({
  hasMeta,
  isReviewed,
  onReviewedChange,
  onGenerated,
}) => {
  const target = useMetaTarget();
  const processedMeta = useAppSelector((state) => state.wave.processedMeta);
  const generateMutation = useGenerateMetaMutation(onGenerated);
  const [reviewError, setReviewError] = useState<string | null>(null);

  const getGenerateState = (): TaskState => {
    if (generateMutation.isPending) return STEP_PROGRESS_STATE.RUNNING;
    if (generateMutation.isError) return STEP_PROGRESS_STATE.ERROR;
    return hasMeta ? STEP_PROGRESS_STATE.COMPLETE : STEP_PROGRESS_STATE.ACTIVE;
  };

  // Validated when the user confirms (not on every keystroke, the meta is ~1 MB), so a
  // broken edit is caught here instead of failing at Publish.
  const handleReviewedChange = (isChecked: boolean) => {
    if (isChecked && !parseMeta(processedMeta)) {
      setReviewError('The processed meta is not a valid JSON object. Fix the errors on the right first.');
      return;
    }
    setReviewError(null);
    onReviewedChange(isChecked);
  };

  const tasks: StepTask[] = [
    {
      id: 'generate',
      title: 'Generate meta',
      state: getGenerateState(),
      content: (
        <Flex direction="column" gap={3}>
          <Hint>
            {hasMeta
              ? 'Regenerating reloads both sides and replaces any edits on the right.'
              : 'Loads the current meta from S3 and processes it for this wave.'}
          </Hint>
          {generateMutation.isError && (
            <ErrorText>{`Couldn't generate the meta: ${getMetaProcessingErrorMessage(generateMutation.error)}`}</ErrorText>
          )}
          <Box>
            <Button
              variant={hasMeta ? 'outline' : 'solid'}
              onClick={() => target && generateMutation.mutate(target)}
              isLoading={generateMutation.isPending}
              loadingText="Generating..."
              isDisabled={!target}
            >
              {hasMeta ? 'Regenerate' : 'Generate meta'}
            </Button>
          </Box>
        </Flex>
      ),
    },
    {
      id: 'review',
      title: 'Review meta',
      state: isReviewed ? STEP_PROGRESS_STATE.COMPLETE : hasMeta ? STEP_PROGRESS_STATE.ACTIVE : STEP_PROGRESS_STATE.UPCOMING,
      content: (
        <Flex direction="column" gap={3}>
          <Hint>Compare the current meta (left) with the processed meta (right) and make any changes on the right.</Hint>
          <Checkbox
            size="sm"
            isChecked={isReviewed}
            isDisabled={!hasMeta}
            onChange={(event) => handleReviewedChange(event.target.checked)}
          >
            I&apos;ve reviewed the processed meta and want to proceed to publish it
          </Checkbox>
          {reviewError && <ErrorText>{reviewError}</ErrorText>}
          {isReviewed && <Hint>Use Next to publish it to QA.</Hint>}
        </Flex>
      ),
    },
  ];

  return <StepProgress tasks={tasks} />;
};
